import { useMemo } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import { getSafeHtmlPreviewDocument } from '../utils/htmlSanitization';

const LANGUAGES = new Set([
  'bash',
  'shell',
  'sh',
  'python',
  'py',
  'json',
  'yaml',
  'yml',
]);

function canHighlight(code: string, language: string) {
  // ponytail: Color at most 32K characters synchronously; use a worker for larger blocks.
  // Prism/HTML parsing normalize these characters; React preserves them verbatim.
  return (
    code.length <= 32768 &&
    !code.includes('\0') &&
    !code.includes('\r') &&
    !code.includes('\u00a0') &&
    LANGUAGES.has(language.toLowerCase())
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Shared with MarkdownIt's fenced-code renderer.
export function highlightApprovalCode(code: string, language = ''): string {
  if (!canHighlight(code, language)) {
    return code
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
  const name =
    language.toLowerCase() === 'sh' ? 'bash' : language.toLowerCase();
  return getSafeHtmlPreviewDocument(
    Prism.highlight(code, Prism.languages[name], name)
  );
}

export function ApprovalCode({
  code,
  language = '',
}: {
  code: string;
  language?: string;
}) {
  const html = useMemo(
    () =>
      canHighlight(code, language)
        ? highlightApprovalCode(code, language)
        : null,
    [code, language]
  );
  return (
    <pre className="approval-code">
      {html === null ? (
        <code>{code}</code>
      ) : (
        <code dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </pre>
  );
}
